import React, { useRef } from 'react';

import { Box, Button, ButtonBase, styled } from '@mui/material';
import { Delete, DragIndicator } from '@mui/icons-material';
import TextField from 'components/FormElements/AppTextInput';
import DraggableList from 'components/DraggableList';
import { Row } from 'further-ui/components/Layout';

type KeyValueItem = { fieldName: string; value: string };

type Props = {
  value: Array<KeyValueItem>;
  onChange: (items: Array<KeyValueItem>) => void;
  fieldNamePlaceholder?: string;
  valuePlaceholder?: string;
  addLabel?: string;
};

const StyledDraggableList = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const AddButtonWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasItems',
})<{ hasItems: boolean }>(({ theme, hasItems }) => ({
  marginTop: hasItems ? theme.spacing(theme.spacingSizes.sm) : 0,
}));

const KeyValueList: React.FC<Props> = ({
  value,
  onChange,
  fieldNamePlaceholder = 'Enter field name',
  valuePlaceholder = 'Enter value',
  addLabel = 'Add new',
}) => {
  const nextKey = useRef(0);
  const keys = useRef<Array<number>>([]);

  while (keys.current.length < value.length) {
    keys.current.push(nextKey.current++);
  }

  const updateItem = (index: number, patch: Partial<KeyValueItem>) => {
    onChange(
      value.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const removeItem = (index: number) => {
    keys.current.splice(index, 1);
    onChange(value.filter((_, i) => i !== index));
  };

  const appendItem = () => {
    onChange([...value, { fieldName: '', value: '' }]);
  };

  const moveItem = (oldIndex: number, newIndex: number) => {
    const updatedKeys = [...keys.current];
    const [removedKey] = updatedKeys.splice(oldIndex, 1);
    updatedKeys.splice(newIndex, 0, removedKey);
    keys.current = updatedKeys;

    const updated = [...value];
    const [removed] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, removed);
    onChange(updated);
  };

  return (
    <Box>
      <StyledDraggableList
        onReorder={({ oldIndex, newIndex }) => {
          moveItem(oldIndex, newIndex);
        }}
      >
        {value.map((item, index) => (
          <DraggableList.Item
            withDragHandle
            key={keys.current[index]}
            id={index.toString()}
          >
            <Row spacing="sm">
              <TextField
                value={item.fieldName}
                onChange={(e) => {
                  updateItem(index, { fieldName: e.target.value });
                }}
                multiline
                rows={3}
                size="small"
                placeholder={fieldNamePlaceholder}
              />
              <TextField
                value={item.value}
                onChange={(e) => {
                  updateItem(index, { value: e.target.value });
                }}
                multiline
                rows={3}
                size="small"
                placeholder={valuePlaceholder}
              />
              <Row centered justify="center">
                <ButtonBase
                  onClick={() => {
                    removeItem(index);
                  }}
                  aria-label="Remove item"
                >
                  <Delete />
                </ButtonBase>
                <DraggableList.Handle>
                  <ButtonBase>
                    <DragIndicator />
                  </ButtonBase>
                </DraggableList.Handle>
              </Row>
            </Row>
          </DraggableList.Item>
        ))}
      </StyledDraggableList>
      <AddButtonWrapper hasItems={value.length > 0}>
        <Button onClick={appendItem} size="large" variant="outlined">
          {addLabel}
        </Button>
      </AddButtonWrapper>
    </Box>
  );
};

export default KeyValueList;
